import { ProfilePhoneTransferModal } from '@/features/ProfilePhoneTransfer/ProfilePhoneTransferModal';
import { PermissionGate } from '@/shared/ui/PermissionGate/PermissionGate';
import { ProfileEmailBindingModal } from '@/features/ProfileEmailBinding/ProfileEmailBindingModal';
import { BonusOperationModal } from '@/features/BonusAccountOperations/BonusOperationModal';
import { OperatorTaskPanel } from '@/features/operatorTasks/ui/OperatorTaskPanel';
import { AddressRecordActionDialog } from './AddressRecordActionDialog';
import { CompleteOrderModal } from '@/features/Order/CompleteOrderModal/CompleteOrderModal';
import { ManualConfirmModal } from '@/features/Order/ManualConfirmModal/ManualConfirmModal';
import { StartExecutorSearchModal } from '@/features/Order/StartExecutorSearchModal/StartExecutorSearchModal';
import { ApproveExecutorModal } from '@/features/Order/ApproveExecutorModal/ApproveExecutorModal';
import { RemoveExecutorModal } from '@/features/Order/RemoveExecutorModal/RemoveExecutorModal';
import { PayForTaskModal } from '@/features/Order/PayForTaskModel/PayForTaskModal';
import { AssignExecutorModal } from '@/features/Order/AssignExecutorModal/AssignExecutorModal';
import { CreatePaymentModal } from '@/features/Order/CreatePaymentModal/CreatePaymentModal';
import { AuditHistoryDrawer } from '@/features/AuditHistory/ui/AuditHistoryDrawer';
import type { RecordActionDialog } from '../model/types';

interface RecordActionDialogsProps {
    dialog: RecordActionDialog | null;
    onClose: () => void;
    onChanged: () => Promise<void>;
    onTaskOpen: (id: string, editing?: boolean) => void;
}

/** Открывает форму действия выбранной записи */
export const RecordActionDialogs = (
    { dialog, onClose, onChanged, onTaskOpen }: RecordActionDialogsProps
) => {
    if (!dialog) return null;
    if (dialog.kind === 'audit') return <AuditHistoryDrawer
        visible
        productId={dialog.productId}
        onHide={onClose}
    />;

    if (dialog.kind === 'clientEmail' || dialog.kind === 'executorEmail') {
        return <ProfileEmailBindingModal
            key={dialog.record.id}
            target={{
                kind: dialog.kind === 'clientEmail'
                    ? 'client'
                    : 'executor',
                profileId: dialog.record.id ?? '',
                appUserId: dialog.record.appUserId ?? '',
            }}
            initialEmail={dialog.record.email}
            onHide={onClose}
            onChanged={onChanged}
        />;
    }

    if (dialog.kind === 'profilePhone') {
        return <PermissionGate permission={`admin.${dialog.target.kind === 'client'
            ? 'clients'
            : 'executors'}.phone.transfer`}>
            <ProfilePhoneTransferModal
                key={dialog.target.profileId}
                target={dialog.target}
                currentPhone={dialog.currentPhone}
                onHide={onClose}
                onChanged={onChanged}
            />
        </PermissionGate>;
    }

    if (dialog.kind === 'bonus') {
        return <BonusOperationModal
            visible
            kind={dialog.operation}
            clientId={dialog.clientId}
            onHide={onClose}
            onSuccess={onChanged}
        />;
    }

    if (dialog.kind === 'task') {
        return <OperatorTaskPanel
            taskId={dialog.id}
            creating={dialog.creating}
            editing={dialog.editing}
            origin={dialog.origin}
            onClose={onClose}
            onOpen={onTaskOpen}
            onChanged={() => void onChanged()}
        />;
    }

    if (dialog.kind === 'address') {
        return <AddressRecordActionDialog
            record={dialog.record}
            onClose={onClose}
            onChanged={onChanged}
        />;
    }

    const order = dialog.record;
    const orderId = order.id ?? '';

    /** Завершает действие заказа и обновляет текущую страницу списка */
    const closeOrder = () => {
        onClose();
        void onChanged();
    };

    switch (dialog.action) {
        case 'complete':
            return <CompleteOrderModal
                visible
                orderId={orderId}
                onHide={closeOrder}
            />;
        case 'confirmPayment':
            return <ManualConfirmModal
                visible
                orderId={orderId}
                externalPaymentId={String(order.payment?.externalId ?? '')}
                onHide={closeOrder}
            />;
        case 'startSearch':
            return <StartExecutorSearchModal
                visible
                orderId={orderId}
                onHide={closeOrder}
            />;
        case 'approveExecutor':
            return <ApproveExecutorModal
                visible
                orderId={orderId}
                onHide={closeOrder}
            />;
        case 'removeExecutor':
            return <RemoveExecutorModal
                visible
                orderId={orderId}
                onHide={closeOrder}
            />;
        case 'payTask':
            return <PayForTaskModal
                visible
                orderId={orderId}
                taskId={dialog.task?.id ?? null}
                onHide={closeOrder}
            />;
        case 'assignExecutor':
            return <AssignExecutorModal
                visible
                orderId={orderId}
                orderNumber={order.orderNumber ?? ''}
                orderType={order.orderType}
                onHide={closeOrder}
            />;
        case 'createPayment':
            return <CreatePaymentModal
                visible
                orderId={orderId}
                orderNumber={order.orderNumber ?? ''}
                orderType={order.orderType}
                onHide={closeOrder}
            />;
        default:
            return null;
    }
};
